import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Star, Quote } from 'lucide-react';
import styled from 'styled-components';

const Section = styled.section`
  padding: ${props => props.theme.spacing['4xl']} 0;
  background: linear-gradient(to bottom, ${props => props.theme.colors.background.main}, ${props => props.theme.colors.primary.main}0D);
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    padding: 8rem 0;
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing['2xl']};
`;

const ContentWrapper = styled.div`
  max-width: 75rem;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Subtitle = styled.span`
  color: ${props => props.theme.colors.accent.gold};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const Title = styled.h2`
  font-family: ${props => props.theme.typography.fontFamily.display};
  font-size: ${props => props.theme.typography.fontSize['4xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin-top: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing['2xl']};
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize['5xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    font-size: ${props => props.theme.typography.fontSize['6xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    font-size: ${props => props.theme.typography.fontSize['7xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints['2xl']}) {
    font-size: ${props => props.theme.typography.fontSize['8xl']};
  }
`;

const Description = styled.p`
  color: ${props => props.theme.colors.text.muted};
  font-size: ${props => props.theme.typography.fontSize.lg};
  max-width: 42rem;
  margin: 0 auto;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing['2xl']};
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TestimonialCard = styled.div`
  background-color: ${props => props.theme.colors.background.card};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  padding: ${props => props.theme.spacing['2xl']};
  box-shadow: ${props => props.theme.shadows.elegant};
  border: 1px solid ${props => props.theme.colors.border.main};
  position: relative;
  transition: ${props => props.theme.transitions.default};
  
  &:hover {
    border-color: ${props => props.theme.colors.primary.main}4D;
  }
`;

const QuoteIcon = styled.div`
  position: absolute;
  top: -1rem;
  left: ${props => props.theme.spacing['2xl']};
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: white;
  }
`;

const Rating = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-top: ${props => props.theme.spacing.lg};
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    fill: ${props => props.theme.colors.accent.gold};
    color: ${props => props.theme.colors.accent.gold};
  }
`;

const TestimonialText = styled.p`
  color: ${props => props.theme.colors.text.primary};
  line-height: 1.7;
  margin-bottom: ${props => props.theme.spacing['2xl']};
  font-style: italic;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
`;

const Avatar = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: linear-gradient(to bottom right, ${props => props.theme.colors.primary.main}, ${props => props.theme.colors.primary.dark});
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  font-size: ${props => props.theme.typography.fontSize.lg};
`;

const AuthorDetails = styled.div`
  p:first-child {
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    color: ${props => props.theme.colors.text.primary};
    margin: 0 0 ${props => props.theme.spacing.xs} 0;
  }
  
  p:last-child {
    font-size: ${props => props.theme.typography.fontSize.sm};
    color: ${props => props.theme.colors.text.muted};
    margin: 0;
  }
`;

const Date = styled.p`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.text.muted};
  margin-top: ${props => props.theme.spacing.lg};
  margin-bottom: 0;
`;

const TrustBadge = styled.div`
  margin-top: 3rem;
  text-align: center;
`;

const BadgeContent = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  background-color: ${props => props.theme.colors.background.card};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing['2xl']};
  border-radius: ${props => props.theme.borderRadius.full};
  border: 1px solid ${props => props.theme.colors.border.main};
  
  .stars {
    display: flex;
    gap: -${props => props.theme.spacing.xs};
    
    svg {
      width: 1rem;
      height: 1rem;
      fill: ${props => props.theme.colors.accent.gold};
      color: ${props => props.theme.colors.accent.gold};
    }
  }
  
  span {
    color: ${props => props.theme.colors.text.primary};
    font-weight: ${props => props.theme.typography.fontWeight.medium};
  }
  
  .separator {
    color: ${props => props.theme.colors.text.muted};
  }
  
  .reviews {
    font-size: ${props => props.theme.typography.fontSize.sm};
    color: ${props => props.theme.colors.text.muted};
  }
`;

const TestimonialsSection = () => {
  const { t } = useLanguage();

  const testimonials = [
    {
      name: 'Isabella Rodriguez',
      location: t('testimonials.location1'),
      rating: 5,
      text: t('testimonials.text1'),
      date: t('testimonials.date1'),
    },
    {
      name: 'Sofia Garcia',
      location: t('testimonials.location2'),
      rating: 5,
      text: t('testimonials.text2'),
      date: t('testimonials.date2'),
    },
    {
      name: 'Camila Martinez',
      location: t('testimonials.location3'),
      rating: 5,
      text: t('testimonials.text3'),
      date: t('testimonials.date3'),
    },
  ];

  return (
    <Section>
      <Container>
        <ContentWrapper>
          <SectionHeader>
            <Subtitle>{t('testimonials.subtitle')}</Subtitle>
            <Title>{t('testimonials.title')}</Title>
            <Description>{t('testimonials.description')}</Description>
          </SectionHeader>

          <TestimonialsGrid>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index}>
                <QuoteIcon>
                  <Quote />
                </QuoteIcon>
                <Rating>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} />
                  ))}
                </Rating>
                <TestimonialText>"{testimonial.text}"</TestimonialText>
                <AuthorInfo>
                  <Avatar>
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </Avatar>
                  <AuthorDetails>
                    <p>{testimonial.name}</p>
                    <p>{testimonial.location}</p>
                  </AuthorDetails>
                </AuthorInfo>
                <Date>{testimonial.date}</Date>
              </TestimonialCard>
            ))}
          </TestimonialsGrid>

          <TrustBadge>
            <BadgeContent>
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} />
                ))}
              </div>
              <span>{t('testimonials.rating')}</span>
              <span className="separator">|</span>
              <span className="reviews">{t('testimonials.reviews')}</span>
            </BadgeContent>
          </TrustBadge>
        </ContentWrapper>
      </Container>
    </Section>
  );
};

export default TestimonialsSection;
